// src/pages/AdminPage/modals/EditVideoModal.tsx
import { useState, useEffect, useRef } from "react";
import { X, Upload, Film } from "lucide-react";
import { adminAPI } from "../utils/adminApi";
import type { VideoItem } from "../utils/adminTypes";

const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;

interface VideoForm {
  title: string;
  description: string;
  video_url: string;
  order_index: number;
}

interface EditVideoModalProps {
  video: VideoItem | null; // null = yangi yaratish
  open: boolean;
  onClose: () => void;
  onSave: (id: number | null, data: VideoForm) => Promise<void>;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

const emptyForm: VideoForm = {
  title: "",
  description: "",
  video_url: "",
  order_index: 0,
};

export default function EditVideoModal({
  video,
  open,
  onClose,
  onSave,
  onError,
  onSuccess,
}: EditVideoModalProps) {
  const [form, setForm] = useState<VideoForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [cacheBust, setCacheBust] = useState(0);
  const videoFileRef = useRef<HTMLInputElement>(null);
  const thumbFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (video) {
      setForm({
        title: video.title || "",
        description: video.description || "",
        video_url: video.video_url || "",
        order_index: video.order_index || 0,
      });
      setThumbUrl(video.thumbnail_url);
    } else {
      setForm(emptyForm);
      setThumbUrl(null);
    }
  }, [video, open]);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await onSave(video?.id || null, form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleVideoFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file || !video) return;
    if (!file.type.startsWith("video/")) {
      onError("Faqat video fayl yuklash mumkin");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      onError("Video hajmi 200MB dan oshmasligi kerak");
      return;
    }
    setUploadingVideo(true);
    try {
      const res = await adminAPI.uploadVideoFile(video.id, file);
      setForm((f) => ({ ...f, video_url: res.data.video_url || f.video_url }));
      onSuccess("Video fayl yuklandi");
    } catch {
      onError("Video yuklashda xatolik");
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleThumbFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file || !video) return;
    if (!file.type.startsWith("image/")) {
      onError("Faqat rasm fayl yuklash mumkin");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      onError("Rasm hajmi 5MB dan oshmasligi kerak");
      return;
    }
    setUploadingThumb(true);
    try {
      const res = await adminAPI.uploadVideoThumbnail(video.id, file);
      setThumbUrl(res.data.thumbnail_url || null);
      setCacheBust(Date.now());
      onSuccess("Thumbnail yuklandi");
    } catch {
      onError("Thumbnail yuklashda xatolik");
    } finally {
      setUploadingThumb(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 14px",
    background: "#0D1526",
    border: "1px solid #1E2D45",
    borderRadius: 10,
    color: "#E2E8F0",
    fontSize: 13,
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    color: "#64748B",
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 6,
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 16,
      }}
      onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827",
          border: "1px solid #1E2D45",
          borderRadius: 20,
          padding: 24,
          width: "100%",
          maxWidth: 500,
          maxHeight: "90vh",
          overflowY: "auto",
        }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
          }}>
          <h3 style={{ color: "#fff", fontWeight: 800, fontSize: 16, margin: 0 }}>
            {video ? "Videoni tahrirlash" : "Yangi video"}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "#64748B",
              cursor: "pointer",
            }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Sarlavha *</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            style={inputStyle}
            placeholder="Parallel to'xtash amaliyoti"
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Tavsif</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>YouTube link (ixtiyoriy)</label>
          <input
            value={form.video_url}
            onChange={(e) => setForm({ ...form, video_url: e.target.value })}
            style={inputStyle}
            placeholder="https://youtube.com/watch?v=..."
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Tartib raqami</label>
          <input
            type="number"
            value={form.order_index}
            onChange={(e) =>
              setForm({ ...form, order_index: Number(e.target.value) })
            }
            style={inputStyle}
          />
        </div>

        {video && (
          <div
            style={{
              marginBottom: 20,
              padding: 14,
              background: "#0D1526",
              border: "1px solid #1E2D45",
              borderRadius: 12,
            }}>
            <label style={{ ...labelStyle, marginBottom: 10 }}>
              Fayllarni yuklash
            </label>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                onClick={() => videoFileRef.current?.click()}
                disabled={uploadingVideo}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(59,130,246,.3)",
                  background: "rgba(59,130,246,.08)",
                  color: "#3B82F6",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: uploadingVideo ? "not-allowed" : "pointer",
                }}>
                <Film size={13} />
                {uploadingVideo ? "Yuklanmoqda..." : "Video fayl yuklash"}
              </button>
              <button
                onClick={() => thumbFileRef.current?.click()}
                disabled={uploadingThumb}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(34,197,94,.3)",
                  background: "rgba(34,197,94,.08)",
                  color: "#22C55E",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: uploadingThumb ? "not-allowed" : "pointer",
                }}>
                <Upload size={13} />
                {uploadingThumb ? "Yuklanmoqda..." : "Thumbnail yuklash"}
              </button>
            </div>
            {thumbUrl && (
              <img
                src={`${IMG_BASE}/${thumbUrl}${cacheBust ? `?v=${cacheBust}` : ""}`}
                alt="thumbnail"
                style={{
                  width: 96,
                  height: 96,
                  objectFit: "cover",
                  borderRadius: 10,
                  marginTop: 12,
                }}
              />
            )}
            <input
              ref={videoFileRef}
              type="file"
              accept="video/*"
              onChange={handleVideoFileChange}
              style={{ display: "none" }}
            />
            <input
              ref={thumbFileRef}
              type="file"
              accept="image/*"
              onChange={handleThumbFileChange}
              style={{ display: "none" }}
            />
          </div>
        )}
        {!video && (
          <p style={{ color: "#475569", fontSize: 11, marginBottom: 16 }}>
            Fayl yuklash uchun avval videoni yarating, so'ng tahrirlash oynasidan
            fayllarni yuklang.
          </p>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 12,
              border: "1px solid #1E2D45",
              background: "transparent",
              color: "#E2E8F0",
              fontWeight: 700,
              cursor: saving ? "not-allowed" : "pointer",
            }}>
            Bekor
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.title.trim()}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 12,
              border: "none",
              background: "#3B82F6",
              color: "#fff",
              fontWeight: 700,
              cursor: saving || !form.title.trim() ? "not-allowed" : "pointer",
              opacity: saving || !form.title.trim() ? 0.7 : 1,
            }}>
            {saving ?
              "Saqlanmoqda..."
            : video ?
              "Saqlash"
            : "Yaratish"}
          </button>
        </div>
      </div>
    </div>
  );
}
